import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PlayCircle, Plus, Trash2, Eye, EyeOff, Loader2, Video } from "lucide-react";

function ytId(url = "") {
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return m ? m[1] : null;
}

const empty = { title: "", video_url: "", description: "", active: true };

export default function EditorBytesAdminPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => {
    setLoading(true);
    api.get("/admin/editor-bytes").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.video_url.trim()) return toast.error("शीर्षक और वीडियो URL अनिवार्य हैं");
    setSaving(true);
    try {
      await api.post("/admin/editor-bytes", form);
      toast.success("वीडियो जोड़ा गया");
      setForm(empty);
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  const toggle = async (id, active) => { await api.patch(`/admin/editor-bytes/${id}`, { active }); load(); };
  const remove = async (id) => { if (!window.confirm("हटाएँ?")) return; await api.delete(`/admin/editor-bytes/${id}`); load(); };

  return (
    <div data-testid="editor-bytes-admin">
      <div className="mb-6">
        <h1 className="font-devnagri-heading text-3xl font-black text-[#0F172A] flex items-center gap-3">
          <Video className="w-7 h-7 text-[#DC2626]"/> Editor Video Bytes
        </h1>
        <p className="text-sm text-slate-500 mt-1">प्रधान संपादक के छोटे वीडियो — Editor's Column पेज पर दिखेंगे</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* Form */}
        <form onSubmit={submit} className="xl:col-span-2 bg-white rounded-lg border border-slate-200 p-5 space-y-3 h-fit" data-testid="byte-form">
          <h2 className="font-bold text-[#0F172A] flex items-center gap-2"><Plus className="w-4 h-4"/>नया वीडियो जोड़ें</h2>
          <Field label="शीर्षक *"><Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="जैसे: संपादकीय — छत्तीसगढ़ में बदलाव की बयार" data-testid="byte-title"/></Field>
          <Field label="वीडियो URL * (YouTube)"><Input value={form.video_url} onChange={(e) => set("video_url", e.target.value)} placeholder="https://youtu.be/... या https://youtube.com/watch?v=..." data-testid="byte-url"/></Field>
          <Field label="विवरण"><Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="वैकल्पिक — 1-2 पंक्ति में सारांश" data-testid="byte-desc"/></Field>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} data-testid="byte-active"/>
            प्रकाशित / Live
          </label>
          {ytId(form.video_url) && (
            <div className="rounded-md overflow-hidden aspect-video bg-black">
              <iframe title="preview" width="100%" height="100%" src={`https://www.youtube.com/embed/${ytId(form.video_url)}`} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen/>
            </div>
          )}
          <Button type="submit" disabled={saving} className="w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white" data-testid="byte-submit">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Plus className="w-4 h-4 mr-2"/>}
            जोड़ें
          </Button>
        </form>

        {/* List */}
        <div className="xl:col-span-3">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="font-bold text-[#0F172A]">प्रकाशित वीडियो</h2>
            <div className="h-px bg-slate-200 flex-1"></div>
            <span className="text-xs text-slate-500">{items.length}</span>
          </div>
          {loading ? (
            <div className="text-slate-500 py-10 text-center">लोड हो रहा है…</div>
          ) : items.length === 0 ? (
            <div className="text-center py-14 bg-white border border-dashed border-slate-300 rounded-lg">
              <PlayCircle className="w-14 h-14 text-slate-300 mx-auto mb-3"/>
              <div className="font-bold text-slate-700">कोई वीडियो नहीं</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {items.map((v) => (
                <div key={v.id} className={`bg-white rounded-lg border overflow-hidden ${v.active ? "border-slate-200" : "border-slate-200 opacity-60"}`} data-testid={`byte-${v.id}`}>
                  {v.thumbnail_url ? (
                    <a href={v.video_url} target="_blank" rel="noopener noreferrer" className="block aspect-video bg-black relative">
                      <img src={v.thumbnail_url} alt={v.title} className="w-full h-full object-cover"/>
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/40 transition"><PlayCircle className="w-14 h-14 text-white drop-shadow-lg"/></div>
                    </a>
                  ) : <div className="aspect-video bg-slate-100"/>}
                  <div className="p-3">
                    <div className="font-bold text-sm text-[#0F172A] line-clamp-2">{v.title}</div>
                    {v.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{v.description}</p>}
                    <div className="flex gap-2 mt-3">
                      <Button onClick={() => toggle(v.id, !v.active)} size="sm" variant="outline" className="flex-1 text-xs" data-testid={`toggle-${v.id}`}>
                        {v.active ? <><EyeOff className="w-3 h-3 mr-1"/>बंद करें</> : <><Eye className="w-3 h-3 mr-1"/>चालू करें</>}
                      </Button>
                      <Button onClick={() => remove(v.id)} size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`del-${v.id}`}>
                        <Trash2 className="w-3.5 h-3.5"/>
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest font-admin text-slate-500 font-bold mb-1 block">{label}</label>
      {children}
    </div>
  );
}
